import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAnalytics,
  fetchAnalytics,
  selecthasMoreAnalytics,
  selectAnalytics,
  selectAnalyticsLoading,
} from "../../../../store/slices/onboarding-survey-analytics";
import AuditAnalyticsHeader from "../audit-analytics-header";
import "../../styles.css";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import moment from "moment";
import { Body } from "../../../../components/main-body/body";
import classNames from "classnames";
import "../../../../components/main-body/body.css";
import { Progress } from "antd";
import AnalyticsImages from "../../../../assets/analytics-images/index";
import ApiOnboardingsManagement from "../../../../api/onboarding-management";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiSavedViews from "../../../../api/saved-views";
import AnalyticsCard from "./analytics-card/analytics-card";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import ApiSubjectsDashboard from "../../../../api/subjects-dashboard";
import { selectSettings } from "../../../../store/slices/settings";
import { selectCurrentProject } from "../../../../store/slices/user";
import PreSignInActivity from "../pre-sign-in-activity/pre-sign-in-activity";

const sortViews = (list = []) =>
  [...list].sort((a, b) => {
    if (Boolean(a.isDefault) !== Boolean(b.isDefault)) {
      return a.isDefault ? -1 : 1;
    }

    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const normalizeSorting = (sorting) => ({
  sortField: sorting?.sortField || null,
  sortOrder: sorting?.sortOrder || null,
});

const OnboardingsAnalyticsTable = ({ navigation }) => {
  const analytics = useSelector(selectAnalytics);
  const hasMore = useSelector(selecthasMoreAnalytics);
  const isLoading = useSelector(selectAnalyticsLoading);
  const settings = useSelector(selectSettings);
  const currentProject = useSelector(selectCurrentProject);

  // The pre-sign-in strip and the table share one viewport, and the table sizes its own
  // scroll container in pixels rather than filling the space it is given (see
  // InfinityTable). So the strip is measured and its height handed over, instead of
  // hardcoding a number that would drift the moment its copy wraps to another line, the
  // window is resized, or the block renders nothing at all for a non-admin.
  const preSignInRef = useRef(null);
  const [preSignInHeight, setPreSignInHeight] = useState(0);

  useEffect(() => {
    const element = preSignInRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(([entry]) => {
      setPreSignInHeight(Math.round(entry.contentRect.height));
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const defaultFiltersRef = useRef({
    period: [
      {
        start: moment().subtract(30, "days").utc().toISOString(),
        end: moment().utc().toISOString(),
      },
    ],
  });

  const defaultSortingRef = useRef({
    sortField: null,
    sortOrder: null,
  });

  const sortingOptions = [
    { value: "title", label: t("analytics.table.message") },
    { value: "lastRespondedAt", label: t("analytics.table.last_response") },
    { value: "scenarioTitle", label: t("analytics.table.scenario") },
    { value: "total", label: t("analytics.table.total") },
    { value: "completionRate", label: t("analytics.table.adherence") },
  ];

  const defaultFilters = defaultFiltersRef.current;
  const defaultSorting = defaultSortingRef.current;

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);

  const [sorting, setSorting] = useState(defaultSorting);

  const [scenariosList, setScenarioList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [selectedAnalytics, setSelectedAnalytics] = useState({});
  const [cohortsList, setCohortsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  const [savedViewsItems, setSavedViewsItems] = useState([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);

  const isSitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;

  const SAVED_VIEW_SCOPE = "analytics_onboardings";

  const safeFilters = filters || defaultFilters;
  const safeSorting = normalizeSorting(sorting);

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(
        r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })) || []
      );
      setSitesList(
        r.data.sites?.map((c) => ({ id: c.id, name: c.name })) || []
      );
    });
  };

  const handleSelectAnalytics = (message) => {
    handleNavigate(`/analytics/monitoring/onboardings/${message.id}`);
    setSelectedAnalytics(message);
    setOpen(true);
  };

  const closeTaskDrawer = () => {
    handleNavigate(`/analytics/monitoring/onboardings`);
    setOpen(false);
    setSelectedAnalytics({});
  };

  const fetchData = (
    currentPage = page,
    query = searchValue,
    currentFilters = safeFilters,
    reset = false
  ) => {
    if (!currentFilters) return;

    if (reset) {
      dispatch(clearAnalytics());
    }

    dispatch(
      fetchAnalytics({
        query,
        page: currentPage,
        ...currentFilters,
        createdAtEnd:
          currentFilters.period?.[currentFilters.period.length - 1]?.end,
        createdAtStart:
          currentFilters.period?.[currentFilters.period.length - 1]?.start,
      })
    );

    setPage(currentPage + 1);
  };

  const loadSavedViews = useCallback(async () => {
    if (!currentProject?.id) {
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting(defaultSorting);
      setFiltersReady(true);
      return;
    }

    setSavedViewsLoading(true);

    try {
      const { data } = await ApiSavedViews.getSavedViews({
        projectId: currentProject.id,
        scope: SAVED_VIEW_SCOPE,
      });

      const list = sortViews(data?.savedViews || []);
      setSavedViewsItems(list);

      const defaultView = list.find((item) => item.isDefault);

      if (defaultView) {
        setSelectedViewId(defaultView.id);
        setFilters(defaultView.filters || {});
        setSorting(normalizeSorting(defaultView.sorting));
      } else {
        setSelectedViewId(null);
        setFilters(defaultFilters);
        setSorting(defaultSorting);
      }
    } catch (error) {
      console.error("Failed to load saved views", error);
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting(defaultSorting);
    } finally {
      setSavedViewsLoading(false);
      setFiltersReady(true);
      setPage(1);
    }
  }, [currentProject?.id, defaultFilters, defaultSorting]);

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    setPage(1);
    fetchData(1, value, safeFilters, true);
  };

  const fetchScenarioList = () => {
    ApiOnboardingsManagement.getOnboardingsSimple().then((r) => {
      setScenarioList(
        (r.data.onboardings || []).map((s) => ({
          id: s.id,
          name: s.title,
        }))
      );
    });
  };

  const fetchSubjectList = () => {
    ApiSubjectsManagement.getSubjectsSimple().then((r) => {
      setSubjectsList(
        (r.data.subjects || []).map((s) => ({
          id: s.id,
          name: s.displayId,
        }))
      );
    });
  };

  const updateFilterValue = (filter) => {
    setFilters(filter);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedViewId(null);
    setFilters(defaultFilters);
    setSorting(defaultSorting);
    setPage(1);
  };

  const applySavedViewFilters = (viewState, viewId = null) => {
    setSelectedViewId(viewId);
    setFilters(
      viewState?.filters && Object.keys(viewState.filters).length
        ? viewState.filters
        : {}
    );
    setSorting(normalizeSorting(viewState?.sorting));
    setPage(1);
  };

  const getIcon = (questionType) => {
    switch (questionType) {
      case "message:multi":
        return AnalyticsImages.checkBorder;
      case "message:single":
        return AnalyticsImages.radioChecked;
      case "message:date":
      case "message:time":
        return AnalyticsImages.date;
      case "message:picker":
      case "message:slider":
        return AnalyticsImages.typeSelect;
      case "message:input":
        return AnalyticsImages.text;
      default:
        return AnalyticsImages.typeSelect;
    }
  };

  const getQuestionTypeLabel = (questionType) => {
    switch (questionType) {
      case "message:single":
        return "Single choice";
      case "message:multi":
        return "Multi choice";
      case "message:picker":
        return "Picker";
      case "message:slider":
        return "Slider";
      case "message:date":
        return "Date";
      case "message:time":
        return "Time";
      case "message:input":
        return "Input";
      default:
        return "Unknown";
    }
  };

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const messageId = pathParts[pathParts.length - 1];

    if (messageId && messageId !== "scenarios" && messageId !== "onboardings") {
      const foundMessage = analytics.find((message) => message.id === messageId);

      if (foundMessage) {
        setSelectedAnalytics(foundMessage);
      } else {
        setSelectedAnalytics({ id: messageId });
      }

      setOpen(true);
    } else {
      setOpen(false);
      setSelectedAnalytics({});
    }
  }, [location.pathname, analytics]);

  useEffect(() => {
    fetchScenarioList();
    fetchSubjectList();
    getDashboardFilterItems();
  }, []);

  useEffect(() => {
    loadSavedViews();
  }, [loadSavedViews]);

  useEffect(() => {
    if (!filtersReady || !filters) return;

    dispatch(clearAnalytics());
    fetchData(1, searchValue, filters, true);
  }, [filtersReady, filters]);

  const sortedAnalytics = useMemo(() => {
    const list = Array.isArray(analytics) ? [...analytics] : [];
    const { sortField, sortOrder } = safeSorting || {};

    if (!sortField || !sortOrder) {
      return list;
    }

    const dir = sortOrder === "ASC" ? 1 : -1;

    const getComparableValue = (item) => {
      switch (sortField) {
        case "title":
          return String(item.title || "").toLowerCase();

        case "lastRespondedAt":
          return item.lastRespondedAt
            ? moment(item.lastRespondedAt).valueOf()
            : 0;

        case "scenarioTitle":
          return String(item.scenarioTitle || "").toLowerCase();

        case "total":
          return (item.totalCount || 0) + (item.pendingCount || 0);

        case "completionRate":
          return item.respondedPercentage || 0;

        default:
          return "";
      }
    };

    list.sort((a, b) => {
      const aVal = getComparableValue(a);
      const bVal = getComparableValue(b);

      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });

    return list;
  }, [analytics, safeSorting]);

  const columns = [
    {
      title: t("analytics.table.message"),
      dataIndex: "title",
      sorter: true,
      render: (_, analyticsItem) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              flexShrink: 0,
              width: "40px",
              height: "40px",
              backgroundColor: "#EFEFEF",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={getIcon(analyticsItem.questionType)}
              alt="icon"
              style={{ width: "20px", height: "20px" }}
            />
          </div>
          <div>
            <span style={{ fontWeight: "bold" }}>
              {analyticsItem.title || "N/A"}
            </span>
            <div style={{ fontSize: "12px", color: "#888" }}>
              {getQuestionTypeLabel(analyticsItem.questionType)}
            </div>
          </div>
        </div>
      ),
      key: "title",
      width: "40%",
    },
    {
      title: t("analytics.table.last_response"),
      dataIndex: "lastRespondedAt",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>
          {analyticsItem.lastRespondedAt
            ? `${moment(analyticsItem.lastRespondedAt)
                .utc()
                .format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A"}
        </span>
      ),
      key: "lastRespondedAt",
      width: "20%",
    },
    {
      title: t("analytics.table.scenario"),
      dataIndex: "scenarioTitle",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>{analyticsItem.scenarioTitle || "N/A"}</span>
      ),
      key: "scenarioTitle",
      width: "15%",
    },
    {
      title: t("analytics.table.total"),
      dataIndex: "total",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>{(analyticsItem.totalCount + analyticsItem.pendingCount) || 0}</span>
      ),
      key: "total",
      width: "10%",
    },
    {
      title: t("analytics.table.adherence"),
      dataIndex: "completionRate",
      sorter: true,
      render: (_, analyticsItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Progress
            type="circle"
            percent={analyticsItem.respondedPercentage || 0}
            size={25}
            strokeLinecap="butt"
            strokeWidth={25}
            strokeColor="#2D7FFA"
            railColor="rgba(45, 127, 250, 0.2)"
            format={() => ""}
          />
          {analyticsItem.respondedPercentage || 0}%
        </span>
      ),
      key: "completionRate",
      width: "15%",
    },
  ];

  const handleTableChange = (pagination, tableFilters, sorter) => {
    const nextSortField = sorter?.field || null;
    const nextSortOrder = !sorter?.order
      ? null
      : sorter.order === "ascend"
        ? "ASC"
        : "DESC";

    setSorting({
      sortField: nextSortField,
      sortOrder: nextSortOrder,
    });
  };

  const messageTypes = [
    { id: "message:single", name: "Single Choice" },
    { id: "message:multi", name: "Multi Choice" },
    { id: "message:picker", name: "Picker" },
    { id: "message:slider", name: "Slider" },
    { id: "message:date", name: "Date" },
    { id: "message:input", name: "Input" },
  ];

  const filterItems = [
    {
      groupName: "Content",
      items: [
        { name: "Cohorts", id: "cohortMessageIds", children: cohortsList },
        { name: "Message Type", id: "actionType", children: messageTypes },
        { name: "Scenario", id: "scenarioIds", children: scenariosList },
      ],
    },
    {
      groupName: "Subjects",
      items: [
        { name: "Cohorts", id: "cohortIds", children: cohortsList },
        {
          name: "Date peroid",
          id: "period",
          period: true,
          defaultValue: defaultFilters.period,
        },
        ...(isSitesEnabled
          ? [{ name: "Sites", id: "siteIds", children: sitesList }]
          : []),
      ],
    },
  ];

  const headerProps = useMemo(
    () => ({
      search: {
        value: searchValue,
        onChange: handleUpdateSearchValue,
        placeholder: t("assets.btn.search"),
      },
    }),
    [searchValue]
  );

  return (
    <Body disabledPaddings={true}>
      <AuditAnalyticsHeader
        title={t("analytics.onboardings")}
        filterItems={filterItems}
        updateFilterValue={updateFilterValue}
        header={headerProps}
        filters={safeFilters}
        sortingOptions={sortingOptions}
        savedViews={{
          enabled: true,
          projectId: currentProject?.id,
          scope: SAVED_VIEW_SCOPE,
          filters: safeFilters,
          sorting: safeSorting,
          defaultFilters,
          defaultSorting,
          filterItems,
          items: savedViewsItems,
          loading: savedViewsLoading,
          selectedViewId,
          onSelectedViewChange: setSelectedViewId,
          onApplySavedView: applySavedViewFilters,
          onResetFilters: resetFilters,
          onReloadSavedViews: loadSavedViews,
          disabled: isLoading || savedViewsLoading,
        }}
      />

      {/* LF360-1917. Sits under the page header and above the table: "can people get in"
          introduces "did they finish the onboarding questions". Below the table it would
          be unreachable - the table sizes its scroll container to the viewport and the
          page shell clips with overflow:hidden - so its measured height is handed to the
          table instead. Renders nothing for non-admins, in which case the height is 0 and
          the table gets the full viewport exactly as before. */}
      <div ref={preSignInRef}>
        <PreSignInActivity />
      </div>

      <div className={classNames("lf-content")}>
        <InfinityTable
          data={sortedAnalytics}
          fetchData={() => fetchData(page, searchValue, safeFilters)}
          hasMore={hasMore}
          tableColumns={columns}
          isLoading={isLoading}
          before={navigation}
          onHandleRowClick={handleSelectAnalytics}
          onChange={handleTableChange}
          heightOffset={preSignInHeight}
        />
      </div>

      <AnalyticsCard
        analytics={selectedAnalytics}
        isOpen={isOpen}
        close={closeTaskDrawer}
        messageTypes={messageTypes}
        subjects={subjectsList}
        cohorts={cohortsList}
        initialPeriodFilter={
          safeFilters?.hasOwnProperty("period")
            ? { period: safeFilters.period }
            : {}
        }
        initialCohortsFilter={
          safeFilters?.hasOwnProperty("cohortIds")
            ? { cohortIds: safeFilters.cohortIds }
            : []
        }
      />
    </Body>
  );
};

export default OnboardingsAnalyticsTable;